import { RequestClientInterfaceService } from './request-client.interface.service';
import { AxiosResponse, ResponseType } from 'axios';
import {
    COGNITO_TOKEN_NAME,
    DEALER_PORTAL_URL,
    PERMISSIONS_TOKEN_NAME,
} from '../config';
import { HttpMethod, HttpStatusCode } from '../utils/enums/http-method.enum';

export interface IDealerBulletinSearchPayload {
    dealerId: string;
    brand: string;
    region?: number | null;
    state?: string | null;
    district?: number | null;
    searchType: 'ACTIVE' | 'EXPIRED';
    startDate: string;
    endDate: string;
    title?: string | null;
    bulletinNumber?: string | null;
    modifiedTime?: string | null;
    bulletinType?: number | null;
}

export interface IAdminBulletinSearchPayload {
    searchType: 'ACTIVE' | 'EXPIRED';
    startDate: string;
    endDate: string;
    title?: string | null;
    bulletinNumber?: string | null;
    modifiedTime?: string | null;
    bulletinType?: number | null;
}

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class BulletinsService {
    private readonly dealerPortalUrlStructure = `${DEALER_PORTAL_URL}`;
    private client: RequestClientInterfaceService;
    private headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: '',
    };
    /**
     * Instantiates a new ClientService
     */
    constructor() {
        this.client = new RequestClientInterfaceService();
    }

    public async getBulletinListDealer(
        payload: IDealerBulletinSearchPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getBulletinListDealer`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                ...payload,
                jwtToken: permissionsToken,
            },
            validateStatus: this.validateNotFound,
        };

        return this.client.sendRequest(request);
    }

    public async getBulletinListAdmin(
        payload: IAdminBulletinSearchPayload,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getBulletinListAdmin`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                ...payload,
                jwtToken: permissionsToken,
            },
            validateStatus: this.validateNotFound,
        };

        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the dealer data
     * @param dealerId DealerId for bulletin list
     * @param brand Brand for bulletin list
     * @param state DealerId for bulletin list
     * @param district DealerId for bulletin list
     * @param region DealerId for bulletin list
     */
    public async getBulletinList(
        dealerId: string,
        brand: string,
        state: string,
        district: number,
        region: number,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getBulletinList`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                dealerId,
                brand,
                region,
                state,
                district,
                jwtToken: permissionsToken,
            },
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the bulleting pdf data
     * @param id Bulleting id
     */
    public async getBulletin(id: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getBulletin`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                id,
                jwtToken: permissionsToken,
            },
            responseType: 'blob' as ResponseType,
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Retrieves the documents pdf data
     * @param brand Dealer Brand
     * @param country Dealer Country
     * @param type Document Type
     * @param lang Dealer/Document lang
     */
    public async getDocuments(
        brand: string,
        country: string,
        type: string,
        language: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/getDocumentByType`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                brand,
                country,
                type,
                language,
                jwtToken: permissionsToken,
            },
            responseType: 'blob' as ResponseType,
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Upload the bulleting pdf data
     * @param id Bulleting id
     */
    public async uploadDocument(
        documentId: string,
        documentType: number,
        modifiedBy: string,
        document: any,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/uploadDocument`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const formData = new FormData();
        formData.append('documentId', documentId);
        formData.append('documentType', String(documentType));
        formData.append('modifiedBy', modifiedBy);
        formData.append('jwtToken', permissionsToken || '');
        formData.append('document', document, document.name);

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: formData,
            responseType: 'json' as ResponseType,
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    public async upsertBulletin(payload: {
        id?: string;
        documentId?: string;
        documentType: number;
        bulletinType: number;
        bulletinNumber: string;
        distributionId: string;
        title: string;
        validFromDate: string;
        validThroughDate: string | null;
        language: number;
        country: string;
        visualStyles: string;
        document?: string;
    }): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/upsertBulletin`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                ...payload,
                jwtToken: permissionsToken,
            },
            responseType: 'json' as ResponseType,
            validateStatus: this.validateNotFound,
        };

        return this.client.sendRequest(request);
    }

    /**
     * Upload document using S3 key reference (for files > 3MB).
     *
     * @param documentId Document UUID
     * @param documentType Document type number
     * @param modifiedBy User uploading the document
     * @param s3Key S3 object key where the file was uploaded
     */
    public async uploadDocumentWithS3Key(
        documentId: string,
        documentType: number,
        modifiedBy: string,
        s3Key: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/uploadDocument`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                documentId,
                documentType,
                modifiedBy,
                s3Key,
                jwtToken: permissionsToken,
            },
            responseType: 'json' as ResponseType,
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Get a presigned S3 URL for uploading a document directly to S3.
     *
     * @param documentId Document UUID
     * @param documentType Document type number
     * @param fileName Name of the file to upload
     */
    public async getUploadPresignedUrl(
        documentId: string,
        documentType: number,
        fileName: string,
    ): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/uploadDocument`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                documentId,
                documentType,
                fileName,
                jwtToken: permissionsToken,
            },
            responseType: 'json' as ResponseType,
            validateStatus: this.validateNotFound,
        };
        return this.client.sendRequest(request);
    }

    /**
     * Permanently deletes a bulletin (and its document when unused elsewhere)
     * @param id Bulletin id
     */
    public async removeBulletin(id: string): Promise<AxiosResponse> {
        const url = `${this.dealerPortalUrlStructure}/removeBulletin`;
        const cognitoToken = window.sessionStorage.getItem(COGNITO_TOKEN_NAME);
        const permissionsToken = window.sessionStorage.getItem(
            PERMISSIONS_TOKEN_NAME,
        );

        const request = {
            method: HttpMethod.post,
            url,
            headers: {
                ...this.headers,
                Authorization: `Bearer ${cognitoToken}`,
            },
            data: {
                id,
                jwtToken: permissionsToken,
            },
            responseType: 'json' as ResponseType,
        };

        return this.client.sendRequest(request);
    }

    validateNotFound(status: number) {
        return (
            status == HttpStatusCode.NOT_FOUND ||
            status == HttpStatusCode.SUCCESS
        );
    }

    public abort() {
        this.client.controller.abort();
    }
}
